import React, { FC } from "react";
import { Box, Text, VStack } from "@chakra-ui/react";
import { Link } from "src/atoms/PrismicLink";
import {
  MenuSubItem,
  IllustrationCardColumn,
  LinksListColumn,
  NavPromoColumn,
} from "../types";

interface MobileDetailPanelProps {
  item: MenuSubItem;
}

/**
 * Renders header_fragment body columns stacked, in the exact order
 * the content editor arranged them in Prismic.
 */
export const MobileDetailPanel: FC<MobileDetailPanelProps> = ({ item }) => {
  const fragmentData = item?.header_fragment?.document?.data;

  if (!fragmentData) {
    return (
      <Box>
        {item?.sub_menu_label && (
          <Text
            fontWeight="semibold"
            fontSize="font-18"
            mt="space-16"
            mb="space-24"
          >
            {item.sub_menu_label}
          </Text>
        )}
        <Text color="red.500" fontWeight="bold">
          Missing Content!
        </Text>
        <Text mt="space-8" fontSize="sm" color="grey-600">
          The Header Fragment for this tab is either not linked in Prismic, or
          it is currently Unpublished.
        </Text>
      </Box>
    );
  }

  return (
    <VStack align="stretch" spacing="space-32">
      {item?.sub_menu_label && (
        <Box pt="space-16">
          <Text fontWeight="semibold" fontSize="font-18" lineHeight="1.2">
            {item.sub_menu_label}
          </Text>
        </Box>
      )}
      {fragmentData.body?.slice(0, 4).map((rawCol, colIdx: number) => {
        const columnSpacing = {
          borderBottom: "1px",
          borderColor: "monochrome-200",
          paddingBottom: "space-32",
          _last: { borderBottom: "none", paddingBottom: 0 },
        };

        if (rawCol.__typename.includes("IllustrationCardColumn")) {
          const col = rawCol as IllustrationCardColumn;
          const isIllustration = col.primary?.is_illustration !== false;
          return (
            <Link
              key={colIdx}
              href={col.primary?.link?.url}
              display="block"
              _hover={{ opacity: 0.9 }}
              {...columnSpacing}
            >
              {isIllustration ? (
                <Box
                  bg="secondary-stone-50"
                  p="space-16"
                  borderRadius="md"
                  h="200px"
                  display="flex"
                  flexDirection="column"
                  justifyContent="space-between"
                  overflow="hidden"
                >
                  {col.primary?.graphics?.url && (
                    <Box
                      flex="1"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      overflow="hidden"
                    >
                      <img
                        src={col.primary.graphics.url}
                        alt={col.primary.graphics.alt}
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          width: "auto",
                          height: "auto",
                          objectFit: "contain",
                          mixBlendMode: "multiply",
                        }}
                      />
                    </Box>
                  )}
                  <Text
                    color="btn-secondary-text"
                    fontSize="font-14"
                    mt="space-16"
                    fontWeight="semibold"
                    lineHeight="1.2"
                  >
                    {col.primary?.label}
                  </Text>
                </Box>
              ) : (
                <Box
                  position="relative"
                  borderRadius="md"
                  overflow="hidden"
                  h="200px"
                >
                  {col.primary?.graphics?.url && (
                    <img
                      src={col.primary.graphics.url}
                      alt={col.primary.graphics.alt}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  )}
                  <Box
                    position="absolute"
                    top={0}
                    left={0}
                    w="100%"
                    h="100%"
                    bg="linear-gradient(180deg, rgba(102, 102, 102, 0) 0%, #000000 100%)"
                    opacity={0.6}
                  />
                  <Text
                    position="absolute"
                    bottom="space-12"
                    left="space-12"
                    color="white"
                    fontWeight="semibold"
                    fontSize="font-14"
                    lineHeight="1.2"
                  >
                    {col.primary?.label}
                  </Text>
                </Box>
              )}
            </Link>
          );
        }

        if (rawCol.__typename.includes("LinksListColumn")) {
          const col = rawCol as LinksListColumn;
          return (
            <Box key={colIdx} {...columnSpacing}>
              {col.primary?.title && (
                <Text
                  fontWeight="semibold"
                  mb="space-24"
                  color="monochrome-900"
                  fontSize="font-14"
                  textTransform="capitalize"
                  lineHeight="1.2"
                >
                  {col.primary?.title}
                </Text>
              )}
              <VStack align="stretch" spacing="space-8">
                {col.items?.map((linkItem, lIdx: number) => (
                  <Link
                    key={lIdx}
                    href={linkItem.link?.url}
                    fontSize="font-14"
                    fontWeight="medium"
                    color="text-subheader"
                    textDecoration="none !important"
                    _hover={{ color: "primary-green", textDecoration: "none" }}
                  >
                    {linkItem.label}
                  </Link>
                ))}
              </VStack>
              {col.primary?.cta_label && (
                <Link
                  href={col.primary.cta_link?.url}
                  mt="space-16"
                  display="inline-block"
                  fontWeight="medium"
                  variant="link-nav"
                  sx={{
                    textDecorationThickness: "1px",
                    textUnderlineOffset: "1px",
                    _hover: {
                      textDecorationThickness: "1px",
                      textUnderlineOffset: "1px",
                    },
                  }}
                >
                  {col.primary.cta_label}
                </Link>
              )}
            </Box>
          );
        }

        if (rawCol.__typename.includes("NavPromoColumn")) {
          const col = rawCol as NavPromoColumn;
          return (
            <Box key={colIdx} {...columnSpacing}>
              {col.items && col.items.length > 0 && (
                <Box mb="space-32">
                  {col.primary?.title && (
                    <Text
                      fontWeight="semibold"
                      mb="space-24"
                      color="monochrome-900"
                      fontSize="font-14"
                      textTransform="capitalize"
                      lineHeight="1.2"
                    >
                      {col.primary.title}
                    </Text>
                  )}
                  <VStack align="stretch" spacing="space-8">
                    {col.items.map((linkItem, lIdx: number) => (
                      <Link
                        key={lIdx}
                        href={linkItem.link?.url}
                        fontSize="font-14"
                        fontWeight="medium"
                        color="text-subheader"
                        textDecoration="none !important"
                        _hover={{
                          color: "primary-green",
                          textDecoration: "none",
                        }}
                      >
                        {linkItem.label}
                      </Link>
                    ))}
                  </VStack>
                </Box>
              )}

              <Link
                href={col.primary?.link?.url}
                display="block"
                _hover={{ opacity: 0.9 }}
              >
                {col.primary?.image?.url && (
                  <Box
                    bg="secondary-stone-50"
                    p={col.primary.is_illustration === false ? "0" : "space-16"}
                    borderRadius="md"
                    mb="space-16"
                    overflow="hidden"
                    h="200px"
                  >
                    <img
                      src={col.primary.image.url}
                      alt={col.primary.image.alt}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit:
                          col.primary.is_illustration === false
                            ? "cover"
                            : "contain",
                        mixBlendMode:
                          col.primary.is_illustration === false
                            ? "normal"
                            : "multiply",
                      }}
                    />
                  </Box>
                )}
                <Text
                  fontWeight="semibold"
                  color="primary-green"
                  fontSize="font-16"
                  lineHeight="1.2"
                >
                  {col.primary?.label}
                </Text>
                {col.primary?.description?.raw?.[0]?.text && (
                  <Text
                    mt="space-8"
                    fontSize="font-14"
                    color="monochrome-600"
                    lineHeight="1.35"
                  >
                    {col.primary.description.raw[0].text}
                  </Text>
                )}
              </Link>
            </Box>
          );
        }

        return null;
      })}
    </VStack>
  );
};

export default MobileDetailPanel;
